import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, RotateCcw, Wand2 } from "lucide-react";
import { HybridFormField } from "@/components/HybridFormField";
import { PromptResult } from "@/components/PromptResult";
import { VisualTranslationHint } from "@/components/VisualTranslationHint";
import { getMasterPromptTemplate } from "@/data/masterPromptTemplates";
import { buildPromptForTemplate, createEmptyForm } from "@/lib/masterPromptBuilder";
import { usePromptLibrary } from "@/hooks/usePromptLibrary";

const autoHandledVisualFieldIds = new Set(["kieli", "realismi", "jatkuvuus", "negatiiviset"]);

const visibleTemplateFields = (template) => {
  const isVisualTemplate = ["ai-kuvapromptit-master", "ai-videopromptit-master"].includes(template.id);
  return isVisualTemplate ? template.lomakekentat.filter((field) => !autoHandledVisualFieldIds.has(field.id)) : template.lomakekentat;
};

export default function TemplateBuilderPage() {
  const { templateId } = useParams();
  const template = getMasterPromptTemplate(templateId);
  const emptyForm = useMemo(() => (template ? createEmptyForm(template) : {}), [template]);
  const [form, setForm] = useState(emptyForm);
  const [prompt, setPrompt] = useState("");
  const { savePrompt } = usePromptLibrary();

  useEffect(() => {
    setForm(emptyForm);
    setPrompt("");
  }, [emptyForm]);

  const update = useCallback((key, nextValue) => setForm((current) => ({ ...current, [key]: nextValue })), []);
  const updateOther = useCallback((key, nextValue) => setForm((current) => ({ ...current, [`${key}__other`]: nextValue })), []);
  const selectedAiTool = form.aiTyokalu === "Muu, mikä?" ? form.aiTyokalu__other : form.aiTyokalu;
  const showVisualTranslationHint = Boolean(prompt) && ["ai-kuvapromptit-master", "ai-videopromptit-master", "suno-musiikkipromptit", "aanitehostepromptit"].includes(template?.id);
  const fieldGroups = useMemo(() => (template ? visibleTemplateFields(template) : []).reduce((groups, field) => {
    const section = field.section || "Täytettävät tiedot";
    if (!groups[section]) groups[section] = [];
    groups[section].push(field);
    return groups;
  }, {}), [template]);
  const fillExample = useCallback(() => {
    if (!template) return;
    setForm({ ...emptyForm, ...template.esimerkkitaytto });
    setPrompt("");
  }, [emptyForm, template]);
  const resetForm = useCallback(() => {
    setForm(emptyForm);
    setPrompt("");
  }, [emptyForm]);
  const generatePrompt = useCallback(() => {
    if (!template) return;
    setPrompt(buildPromptForTemplate(form, template));
  }, [form, template]);
  const saveTemplatePrompt = useCallback(({ title, prompt: finalPrompt }) => {
    if (!template) return;
    savePrompt({ title, type: template.kategoria, prompt: finalPrompt, aiTool: selectedAiTool });
  }, [savePrompt, selectedAiTool, template]);

  if (!template) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8" data-testid="template-builder-not-found">
        <p className="text-sm font-extrabold uppercase text-[#D97757]" data-testid="template-builder-not-found-kicker">Pohjaa ei löytynyt</p>
        <h1 className="mt-2 font-heading text-4xl font-bold text-[#2C2F2E]" data-testid="template-builder-not-found-title">Tätä promptipohjaa ei ole saatavilla</h1>
        <Link to="/pohjat" className="mt-6 inline-flex rounded-full bg-[#4A6753] px-5 py-3 text-sm font-bold text-white" data-testid="template-builder-back-link">Takaisin promptipohjiin</Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8" data-testid="template-builder-page">
      <Link to="/pohjat" className="mb-8 inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-bold text-[#4A6753] ring-1 ring-[#E8E5D9] transition-transform duration-200 hover:-translate-y-0.5" data-testid="template-builder-back-button">
        <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Takaisin pohjiin
      </Link>

      <div className="mb-8 max-w-3xl" data-testid="template-builder-heading-block">
        <p className="text-sm font-extrabold uppercase text-[#D97757]" data-testid="template-builder-kicker">Valmis pohja</p>
        <h1 className="mt-2 font-heading text-4xl font-bold text-[#2C2F2E] sm:text-5xl" data-testid="template-builder-title">{template.nimi}</h1>
        <p className="mt-4 text-base leading-7 text-[#5C6360]" data-testid="template-builder-description">{template.lyhytKuvaus}</p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(420px,0.85fr)]" data-testid="template-builder-workspace">
        <section className="rounded-[1.75rem] border border-[#E8E5D9] bg-white p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)] sm:p-7" data-testid="template-builder-form-section">
          <div className="mb-6 rounded-[1.25rem] bg-[#F5F3EC] p-5" data-testid="template-builder-summary">
            <p className="text-xs font-bold uppercase text-[#D97757]" data-testid="template-builder-category">{template.kategoria}</p>
            <h2 className="mt-2 font-heading text-2xl font-bold text-[#2C2F2E]" data-testid="template-builder-summary-title">Mihin tämä auttaa?</h2>
            <p className="mt-2 text-sm leading-7 text-[#5C6360]" data-testid="template-builder-purpose">{template.kayttotarkoitus}</p>
          </div>

          <div className="mb-6 flex flex-wrap gap-2" data-testid="template-builder-helper-actions">
            <button type="button" onClick={fillExample} className="inline-flex items-center gap-2 rounded-full bg-[#D97757] px-4 py-3 text-sm font-bold text-white transition-transform duration-200 hover:-translate-y-0.5" data-testid="template-builder-fill-example-button">
              <Wand2 className="h-4 w-4" aria-hidden="true" /> Täytä esimerkillä
            </button>
            <button type="button" onClick={resetForm} className="inline-flex items-center gap-2 rounded-full bg-[#F5F3EC] px-4 py-3 text-sm font-bold text-[#2C2F2E] transition-transform duration-200 hover:-translate-y-0.5" data-testid="template-builder-reset-button">
              <RotateCcw className="h-4 w-4" aria-hidden="true" /> Tyhjennä
            </button>
          </div>

          <div className="mb-4" data-testid="template-builder-form-heading">
            <h2 className="font-heading text-2xl font-bold text-[#2C2F2E]" data-testid="template-builder-form-title">Täytettävät tiedot</h2>
            <p className="mt-1 text-sm leading-6 text-[#5C6360]" data-testid="template-builder-form-description">Täytä vain ne kohdat, jotka tiedät. Realismi-, jatkuvuus- ja vältettävät ohjeet lisätään automaattisesti promptiin.</p>
          </div>

          <div className="space-y-7" data-testid="template-builder-form-grid">
            {Object.entries(fieldGroups).map(([section, fields], sectionIndex) => (
              <section key={section} className="rounded-[1.25rem] border border-[#E8E5D9] bg-[#FDFCF8] p-4 sm:p-5" data-testid={`template-builder-section-${sectionIndex}`}>
                <h3 className="font-heading text-xl font-bold text-[#2C2F2E]" data-testid={`template-builder-section-title-${sectionIndex}`}>{section}</h3>
                <div className="mt-4 grid grid-cols-1 gap-5 md:grid-cols-2" data-testid={`template-builder-section-grid-${sectionIndex}`}>
                  {fields.map((field) => (
                    <div key={field.id} className={field.type === "textarea" || field.type === "multiselect" ? "md:col-span-2" : ""} data-testid={`template-builder-field-wrapper-${field.id}`}>
                      <HybridFormField
                        field={field}
                        value={form[field.id] || ""}
                        onChange={(nextValue) => update(field.id, nextValue)}
                        otherValue={form[`${field.id}__other`] || ""}
                        onOtherChange={(nextValue) => updateOther(field.id, nextValue)}
                        testId={`template-builder-${field.id}-field`}
                      />
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>

          <button type="button" onClick={generatePrompt} className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#4A6753] px-6 py-4 text-sm font-bold text-white transition-transform duration-200 hover:-translate-y-0.5 sm:w-auto" data-testid="template-builder-generate-button">
            <Wand2 className="h-4 w-4" aria-hidden="true" /> Luo prompti
          </button>
        </section>

        <div data-testid="template-builder-result-column">
          <PromptResult
            title={`${template.nimi} - valmis prompti`}
            prompt={prompt}
            testIdPrefix="template-builder-result"
            onSave={saveTemplatePrompt}
          />
          {showVisualTranslationHint && <VisualTranslationHint testIdPrefix="template-builder-translation-hint" />}
        </div>
      </div>
    </div>
  );
}